<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol lg="3">
                <DInput
                    id="search"
                    v-model="searchValue"
                    :label="__('orders.search_order_number')"
                    :is-valid="searchIsValid"
                    :invalid-feedback="searchError"
                    @keydown.enter.exact.prevent="search"
                />
            </DCol>
            <DCol
                lg="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('orders.search')"
                    @click="search"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
            <DCol
                lg="6"
                class="text-right"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <DRow>
            <DCol lg="12">
                <DCard
                    class="orders-box"
                >
                    <template #default>
                        <DTable
                            :fields="getFields"
                            :items="orderCollection.orders.concat(invalidOrders)"
                            :column-filter="filterColumnEnabled"
                            class="table-responsive"
                            :sorter="isSortable"
                            :sorter-value="sorterValue"
                            @update:sorter-value="updateOrdering"
                        >
                            <template
                                #status-filter
                            >
                                <DMultiSelect
                                    id="status-dropdown"
                                    :options="filterOptions"
                                    selection-type="text"
                                    :selected="selectedStatus"
                                    @update="onFilterChange"
                                />
                            </template>
                            <template #orderNumber="{item}">
                                <td>{{ item.orderNumber }} </td>
                                <td
                                    v-if="!item.valid"
                                    :colspan="colspan"
                                >
                                    <DTag
                                        :label="__('orders.invalid_order')"
                                        color="danger"
                                    /> {{ item.error }}
                                </td>
                            </template>
                            <template #payment="{item}">
                                <td v-if="item.valid">
                                    {{ item.totalAmount }}<br>
                                    <DTag
                                        v-if="item.customer"
                                        :label="'via ' + item.customer.payment_method"
                                    />
                                </td>
                            </template>
                            <template #location="{item}">
                                <td v-if="item.valid && item.location">
                                    {{ item.location }}
                                </td>
                                <td v-if="item.valid && !item.location" />
                            </template>
                            <template #entryDate="{item}">
                                <td v-if="item.valid && item.entryDate">
                                    {{ setDateFormat(item.entryDate) }}
                                </td>
                                <td v-if="item.valid && !item.entryDate" />
                            </template>
                            <template #status="{item}">
                                <td v-if="item.valid && item.status">
                                    <DTag :label="__('orders.status_'+item.status)" />
                                </td>
                                <td v-if="item.valid && !item.status" />
                            </template>
                            <template #minFraudRecommendation="{item}">
                                <td v-if="item.valid && item.minFraudRecommendation">
                                    <DTag
                                        :label="__('orders.'+item.minFraudRecommendation)"
                                        :color="item.minFraudRecommendation !== 'accept' ? 'danger' : 'secondary'"
                                    />
                                    <br><span
                                        v-if="item.minFraudScore"
                                        style="display: inline-block; padding-top: 5px; font-size: 12px;"
                                    >({{ item.minFraudScore }})</span>
                                </td>
                                <td v-if="item.valid && !item.minFraudRecommendation" />
                            </template>
                            <template #customer="{item}">
                                <td
                                    v-if="item.valid && item.customer"
                                    :style="isOrderOpen(item) && !item.customer.id && item.databaseCustomers.length > 0 ? 'background-color: #ffff66' : '#ffffff'"
                                >
                                    <div v-if="item.customer.company">
                                        {{ item.customer.company }}
                                    </div>
                                    <div v-if="item.customer.firstname || item.customer.lastname">
                                        <span v-if="item.customer.firstname">{{ item.customer.firstname }}</span>
                                        <span v-if="item.customer.lastname">{{ item.customer.lastname }}</span>
                                    </div>
                                    <Link
                                        v-if="item.databaseCustomers.length > 0 && item.customer.id"
                                        :href="$route('customers.customers.show', {customer: item.customer.id})"
                                    >
                                        {{ item.customer.id }}
                                    </Link>
                                    <p v-if="item.customer.id && item.customer.status !== 'active'">
                                        <DTag
                                            :label="__('orders.inactive')"
                                            color="danger"
                                        />
                                    </p>
                                    <div
                                        v-if="isOrderOpen(item) && !item.customer.id && item.databaseCustomers.length > 0 && userPermissions.includes('order.accept')"
                                        style="margin-top: 10px;"
                                    >
                                        <DButton
                                            mini
                                            :label="__('orders.assign_customer')"
                                            @click="displayAssignCustomerDialog(item)"
                                        />
                                    </div>
                                </td>
                                <td v-if="item.valid && !item.customer" />
                            </template>
                            <template #products="{item}">
                                <td v-if="item.valid">
                                    <p
                                        v-for="(product, productIndex) in item.products"
                                        :key="productIndex"
                                    >
                                        - <strong>{{ product.domainName !== null ? product.domainName : product.name }}</strong>
                                    </p>
                                    <p v-if="item.transferId !== undefined && item.transferId !== null">
                                        <Link
                                            :href="$route('contracts.transfers', {transferId: item.transferId})"
                                        >
                                            - Contract transfer:  {{ item.transferId }}
                                        </Link>
                                    </p>
                                </td>
                            </template>
                            <template #orderNote="{item}">
                                <td
                                    v-if="item.orderNote"
                                    class="text-break"
                                >
                                    <span
                                        v-if="item.orderNote.note.length > 50"
                                    >
                                        {{ item.orderNote.note.substring(0, 50) }} ...<a
                                            href="javascript:void(0);"
                                            @click="showNote(item)"
                                        >{{ __('orders.more') }}</a>
                                    </span>
                                    <span v-else>
                                        {{ item.orderNote.note }}
                                    </span>
                                </td>
                                <td v-else />
                            </template>
                            <template #actions="{item}">
                                <td style="display: flex; grid-gap: 5px;">
                                    <DButton
                                        mini
                                        icon="eye"
                                        @click="orderDetail(item)"
                                    />
                                    <DDropdown
                                        toggler-text="..."
                                        color="secondary"
                                        :caret="false"
                                    >
                                        <DDropdownItem
                                            v-if="userPermissions.includes('order.reject') && item.status !== 'accepted'"
                                            @click="rejectOrder(item, false)"
                                        >
                                            {{ __('orders.reject') }}
                                        </DDropdownItem>
                                        <DDropdownItem
                                            v-if="userPermissions.includes('order.reject') && item.status !== 'accepted'"
                                            @click="rejectOrder(item, true)"
                                        >
                                            {{ __('orders.silent_reject') }}
                                        </DDropdownItem>

                                        <span
                                            v-if="itemAcceptable(item) && userPermissions.includes('order.accept')"
                                        >

                                            <DDropdownItem
                                                v-if="!item.hasInvalidPaymentData"
                                                @click="acceptOrder(item, false)"
                                            >
                                                {{ __('orders.accept') }}
                                            </DDropdownItem>

                                            <DDropdownItem
                                                v-if="item.hasInvalidPaymentData"
                                                @click="acceptOrder(item, true)"
                                            >
                                                {{ __('orders.accept_with_banktransfer') }}
                                            </DDropdownItem>

                                        </span>

                                        <DDropdownItem
                                            v-if="(!item.customer || !item.customer.id) && (item.databaseCustomers && item.databaseCustomers.length > 0) && item.status === 'open'"
                                            @click="displayAssignCustomerDialog(item)"
                                        >
                                            {{ __('orders.assign_customer') }}
                                        </DDropdownItem>
                                        <DDropdownItem
                                            @click="showOrderNoteDialog(item)"
                                        >
                                            {{ __('orders.note.label') }}
                                        </DDropdownItem>
                                    </DDropdown>
                                </td>
                            </template>
                        </DTable>
                        <DPagination
                            :active-page="orderCollection.page"
                            :pages="orderCollection.totalPages"
                            align="center"
                            @update:activePage="loadPage"
                        />
                        <DModal
                            v-model:show="showRejectDialog"
                            :title="dialogTitle"
                            :message="dialogMessage"
                            is-confirm
                            is-error
                            @close="showRejectDialog = false"
                            @confirm="rejectCallback"
                        />
                        <DModal
                            v-model:show="showAcceptDialog"
                            :title="dialogTitle"
                            :message="dialogMessage"
                            is-confirm
                            @close="showAcceptDialog = false"
                            @confirm="acceptCallback"
                        />
                        <CustomerAssignDialogModal
                            v-if="showCustomerAssignDialog"
                            :show-modal="showCustomerAssignDialog"
                            :selected-item="selectedItem"
                            :selected-database-customer="selectedDatabaseCustomer"
                            :order-customer="orderCustomer"
                            :database-customers="databaseCustomers"
                            @confirm-modal="assignCustomerToOrder"
                            @close-modal="showCustomerAssignDialog = false"
                        />
                        <OrderNoteCreateModal
                            v-if="showNoteCreateModal"
                            :show-modal="showNoteCreateModal"
                            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
                            :order-note="orderNoteDialog.currentNote"
                            :errors="$page.props.errors.orderNoteDialog"
                            @close="showNoteCreateModal = false"
                            @confirm="saveOrderNote"
                        />
                        <DModal
                            :show="showNoteModal"
                            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
                            is-big
                            @close="showNoteModal = false"
                        >
                            <div class="p-3">
                                <DFormTextarea
                                    :value="selectedItem.orderNote ? selectedItem.orderNote.note : ''"
                                    :disabled="true"
                                    rows="5"
                                />
                            </div>
                        </DModal>
                    </template>
                </DCard>
            </DCol>
        </DRow>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script>
import CustomerAssignDialogModal from '~@/Components/Orders/CustomerAssignDialogModal'
import OrderNoteCreateModal from '~@/Components/Orders/OrderNoteCreateModal'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import { customerBankDetails } from '~@/Composables/sepaMandate'

export default {
    name: 'OsaOrderIndex',
    components: {
        ListViewModal,
        OrderNoteCreateModal,
        CustomerAssignDialogModal
    },
    props: {
        orderCollection: {
            type: Object,
            default () {
                return {}
            }
        },
        invalidOrders: {
            type: Array,
            default () {
                return []
            }
        },
        errors: {
            type: Object,
            default () {
                return {}
            }
        },
        page: {
            type: Number,
            default: 1
        },
        orderClient: {
            type: String,
            default: ''
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        orderBy: {
            type: String,
            default: 'id'
        },
        orderByAsc: {
            type: Boolean,
            default: false
        }
    },
    data () {
        const params = new URLSearchParams(window.location.search)
        const filter = []
        for (const param of params.entries()) {
            if (param[0].match(/^filter[[]\d*[\]]$/)) {
                filter.push(param[1])
            }
        }
        return {
            showRejectDialog: false,
            dialogTitle: '',
            dialogMessage: '',
            acceptCallback: () => { return {} },
            searchValue: params.get('search') ?? '',
            selectedStatus: filter.length === 0 && !params.get('search') ? ['open'] : filter,
            orderCustomer: {},
            databaseCustomers: [],
            selectedDatabaseCustomer: {},
            selectedItem: {},
            orderNoteDialog: {
                currentNote: {}
            },
            showNoteModal: false,
            showCustomerAssignDialog: false,
            showAcceptDialog: false,
            showNoteCreateModal: false,
            rejectCallback: () => { return {} },
            showListViewModal: false,
            orderByData: this.orderBy,
            orderByAscData: this.orderByAsc ? 0 : 1,
            colspan: 6,
            filterColumnEnabled: true,
        }
    },
    computed: {
        filterOptions () {
            return [
                { value: 'open', text: this.__('orders.status_open'), selected: this.selectedStatus.includes('open') },
                { value: 'accepted', text: this.__('orders.status_accepted'), selected: this.selectedStatus.includes('accepted') },
                { value: 'rejected', text: this.__('orders.status_rejected'), selected: this.selectedStatus.includes('rejected') },
                { value: 'confirmed', text: this.__('orders.status_confirmed'), selected: this.selectedStatus.includes('confirmed') }
            ]
        },
        searchError () {
            return this.errors.search ?? this.__('orders.search_error')
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: this.__('settings.list_view.' + item.list + '.' + item.column),
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {},
                        filter: item.column === 'status' && item.show_column === true
                    })
                }
                if (item.column === 'status') {
                    this.filterColumnEnabled = item.show_column
                }
            })

            this.getColSpan()

            return availableList
        },
        sorterValue () {
            return {
                column: this.orderByData,
                asc: !!this.orderByAscData
            }
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        }
    },
    methods: {
        loadPage (page) {
            this.$inertia.get(this.$route('orders.osa.index', { _query: { page, filter: this.selectedStatus, order_by: this.orderByData, order_by_asc: this.orderByAscData ? 0 : 1 } }))
        },
        onFilterChange (filter) {
            this.$inertia.get(this.$route('orders.osa.index', { _query: { filter, page: this.page, order_by: this.orderByData, order_by_asc: this.orderByAscData ? 0 : 1 } }))
        },
        rejectOrder (order, silent) {
            const orderNumber = order.orderNumber
            this.dialogTitle = this.__('orders.reject_order_dialog_title', { orderNumber })
            this.dialogMessage = this.__('orders.reject_dialog_message', { orderNumber })
            this.rejectCallback = () => {
                if (order.valid) {
                    this.$inertia.patch(this.$route('orders.osa.update', { order: order.id, _query: { page: this.page, filter: this.selectedStatus } }), { status: 'rejected', silent: silent })
                } else {
                    this.$inertia.patch(this.$route('orders.osa.reject_invalid', { order: order.id }), { status: 'rejected', silent: silent })
                }
            }
            this.showRejectDialog = true
        },
        showOrderNoteDialog (order) {
            this.selectedItem = order
            this.showNoteCreateModal = true

            this.orderNoteDialog.currentNote = order.orderNote ?? {
                id: null, // new note
                order_id: order.id,
                oder_no: order.orderNumber,
                note: ''
            }
        },
        saveOrderNote (item) {
            // This seems to be overly complex but is the result of not being able
            // to trigger a normal form submit when a form is within a modal...
            // as the confirm button of the modal is outside the form
            if (item.id !== null) {
                this.$inertia.patch(
                    this.$route('ordernotes.update', {
                        orderClient: this.orderClient,
                        orderNote: item.id,
                        _query: { page: this.page, filter: this.selectedStatus }
                    }),
                    item,
                    {
                        errorBag: 'orderNoteDialog',
                        onFinish: () => {
                            // Open again if there is any error
                            this.showNoteCreateModal = !!this.errors.orderNoteDialog
                        }
                    }
                )
            } else {
                this.$inertia.post(this.$route('ordernotes.create', {
                    orderClient: this.orderClient,
                    orderId: this.selectedItem.id,
                    orderNumber: this.selectedItem.orderNumber,
                    _query: { page: this.page, filter: this.selectedStatus }
                }), item, {
                    errorBag: 'orderNoteDialog',
                    onFinish: () => {
                        // Open again if there is any error
                        this.showNoteCreateModal = !!this.errors.orderNoteDialog
                    }
                })
            }
        },
        acceptOrder (order, switchToBanktransfer) {
            const orderNumber = order.orderNumber
            this.dialogTitle = this.__('orders.accept_order_dialog_title', { orderNumber })
            this.dialogMessage = this.__('orders.accept_dialog_message', { orderNumber })
            this.acceptCallback = () => {
                this.$inertia.patch(
                    this.$route(
                        'orders.osa.update', {
                            order: order.id,
                            _query: {
                                page: this.page,
                                filter: this.selectedStatus
                            }
                        }
                    ),
                    { status: 'accepted', useDefaultPaymentMethod: switchToBanktransfer }
                )
            }
            this.showAcceptDialog = true
        },
        searchIsValid (value) {
            if (value.length === 0) {
                return null
            }
            return value !== null && value.trim().length > 0
        },
        search () {
            if (this.searchIsValid(this.searchValue) !== true) {
                return
            }

            this.$inertia.get(this.$route('orders.osa.index', { _query: { search: this.searchValue.trim(), order_by: this.orderByData, order_by_asc: this.orderByAscData ? 0 : 1 } }))
        },
        itemAcceptable (item) {
            const acceptableStatuses = ['open', 'rejected']

            if (acceptableStatuses.includes(item.status) && item.valid) {
                return true
            }

            if (!item.valid && item.hasInvalidPaymentData) {
                return true
            }
        },
        isOrderOpen (item) {
            return item.status === 'open'
        },
        displayAssignCustomerDialog (item) {
            this.selectedItem = item
            this.orderCustomer = item.customer
            this.databaseCustomers = item.databaseCustomers
            this.selectedDatabaseCustomer = item.databaseCustomers[0]
            this.selectedDatabaseCustomer.bankDetails = this.getCustomerBankDetails(this.selectedDatabaseCustomer)
            this.showCustomerAssignDialog = true
        },
        assignCustomerToOrder (item) {
            this.$inertia.post(
                this.$route('orders.osa.assign_customer', {
                    order: item.order.id,
                    customerId: item.customerId
                }),
                {
                    customer_type: item.finalCustomer.customer_type,
                    brand_id: item.finalCustomer.brand_id,
                    title: item.finalCustomer.title,
                    firstname: item.finalCustomer.firstname,
                    lastname: item.finalCustomer.lastname,
                    company: item.finalCustomer.company,
                    address1: item.finalCustomer.address1,
                    address2: item.finalCustomer.address2,
                    postal_code: item.finalCustomer.postal_code,
                    city: item.finalCustomer.city,
                    country_code: item.finalCustomer.country_code,
                    birthday: item.finalCustomer.birthday,
                    phone: item.finalCustomer.phone,
                    payment_method: item.finalCustomer.payment_method,
                    payment_iban: item.finalCustomer.payment_iban,
                    payment_bic: item.finalCustomer.payment_bic,
                    account_owner: item.finalCustomer.account_owner
                }
            )

            this.showCustomerAssignDialog = false
            this.showCustomerAssignConfirmDialog = false
        },
        orderDetail (item) {
            this.$inertia.get(this.$route('orders.details', { order: item.id, client: 'OSA' }))
        },
        getCustomerBankDetails (customer) {
            return customerBankDetails(customer)
        },
        resetFilters () {
            this.$inertia.get(this.$route('orders.osa.index', { _query: { page: this.page } }))
        },
        showNote (order) {
            this.selectedItem = order
            this.showNoteModal = true
        },
        updateOrdering (event) {
            this.$inertia.get(this.$route('orders.osa.index', {
                _query: {
                    page: this.page,
                    search: this.searchValue.trim(),
                    filter: this.selectedStatus,
                    order_by: event.column,
                    order_by_asc: event.asc ? 0 : 1
                }
            }))
        },
        getColSpan () {
            let activeListLength = 0

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    ++activeListLength
                }
            })

            const colspan = activeListLength - 4

            if (colspan < 0) {
                this.colspan = 0

                return
            }

            if (colspan < 4) {
                this.colspan = colspan + 1

                return
            }

            this.colspan = colspan
        }
    }
}
</script>

<style lang="scss">
// Forcing Status Column to be bigger despite having no space.
// Doing it manually because CoreUI can't scale a Column if the space is too tight
.orders-box th:nth-child(3) {
    width: 170px;
}
.database-customers {
    align-items: flex-end;
    gap: 10px;
}
.select-margin {
    margin-bottom: -8px;
}
.select-customer-table th:nth-child(1) {
    width: 20% !important;
}
.select-customer-table th:nth-child(2) {
    width: 40% !important;
}
.select-customer-table th:nth-child(3) {
    width: unset !important;
    border-bottom-width: 1px;
}
.different-customer-data {
    background-color: #ffff66;
}
.select-customer-table th {
    border-bottom: none !important;
}
.orders-blocked-text {
    color: var(--colorDanger);
    font-weight: bold;
}
.table.table thead tr th div#status-dropdown.dropdown.picker.form-multi-select.form-multi-select-with-cleaner.show div.dropdown-menu {
    margin-left: -15px !important;
}
</style>
